import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ref, onValue, get } from "firebase/database";
import { database } from './firebase';
import Header from './Header';
import Footer from './Footer';
import Timeline from './Timeline';
import DatabaseUpdater from './DatabaseUpdater';
import './App.css';

function App() {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(2025); // Default to 2025
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayInfo, setDayInfo] = useState(null);
  const [events, setEvents] = useState({});
  const [selectedDayData, setSelectedDayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpdateStatus, setShowUpdateStatus] = useState(false); // New state for admin mode

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Fetch all events from Firebase when component mounts
  useEffect(() => {
    setLoading(true);
    setError(null);
    
    const eventsRef = ref(database, 'events');
    
    try {
      const unsubscribe = onValue(eventsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          // console.log('Fetched events data:', data);
          setEvents(data);
        } else {
          // console.log('No events data available');
          setEvents({});
        }
        setLoading(false);
      }, (error) => {
        console.error('Error fetching events:', error);
        setError('Failed to load events data. Please try again later.');
        setLoading(false);
      });
      
      // Cleanup subscription on unmount
      return () => unsubscribe();
    } catch (err) {
      console.error('Fatal error setting up Firebase listener:', err);
      setError('Failed to connect to the database. Please check your connection.');
      setLoading(false);
    }
  }, []);

  const handleMonthChange = (e) => {
    setSelectedMonth(parseInt(e.target.value));
    setSelectedDay(null);
    setSelectedDayData(null);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setSelectedDay(null);
    setSelectedDayData(null);
  };

  const handleDayClick = (day, dayNumber) => {
    setSelectedDay(day);
    setDayInfo({day, dayNumber});
    
    // Get detailed data for this day
    const dateKey = formatDate(selectedYear, selectedMonth, day);
    setLoading(true);
    
    // Single-date fetch approach (more efficient for large datasets)
    const dayRef = ref(database, `events/${dateKey}`);
    get(dayRef).then((snapshot) => {
      const dayData = snapshot.val();
      if (dayData && dayData.active) {
        // console.log(`Fetched data for ${dateKey}:`, dayData);
        setSelectedDayData(dayData);
      } else {
        // console.log(`No active data for ${dateKey}`);
        setSelectedDayData(null);
      }
      setLoading(false);
    }).catch((error) => {
      console.error(`Error fetching data for ${dateKey}:`, error);
      setError(`Failed to load data for ${months[selectedMonth]} ${day}, ${selectedYear}`);
      setLoading(false);
    });
    
    // Scroll to event section
    document.getElementById('event-section').scrollIntoView({ behavior: 'smooth' });
  };

  const formatDate = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  // Check if a date has active events
  const hasEvents = (year, month, day) => {
    const dateKey = formatDate(year, month, day);
    return events[dateKey] && events[dateKey].active === true;
  };

  // Determine which term the selected date belongs to
  const getPresidencyTerm = () => {
    if (selectedYear >= 2017 && selectedYear <= 2021) {
      return "First Term";
    } else if (selectedYear >= 2025 && selectedYear <= 2029) {
      return "Second Term";
    }
    return "";
  };

  // Toggle admin mode (shows the database updater status)
  const toggleAdminMode = () => {
    setShowUpdateStatus(!showUpdateStatus);
  };

  // Handle keyboard shortcut for admin mode (Ctrl+Shift+A)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        toggleAdminMode();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showUpdateStatus, toggleAdminMode]);

  if (loading && !selectedDay) {
    return <div className="loading">Loading events data...</div>;
  }

  if (error && !selectedDay) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="app-container">
      {/* Hidden DatabaseUpdater component - always running */}
      <DatabaseUpdater showStatus={showUpdateStatus} />
      
      <Header />
      <nav className="main-nav">
        <div className="nav-container">
          <Link to="/" className="nav-link active">Home</Link>
          <Link to="/about" className="nav-link">About</Link>
          {showUpdateStatus && (
            <button className="admin-button" onClick={toggleAdminMode}>
              Hide Admin Panel
            </button>
          )}
        </div>
      </nav>
      <main className="main-content">
        {/* Month selector */}
        <div className="month-selector">
          <select
            value={selectedMonth}
            onChange={handleMonthChange}
            className="month-dropdown"
          >
            {months.map((month, index) => (
              <option key={index} value={index}>
                {month}
              </option>
            ))}
          </select>
        </div>
        {/* Timeline component */}
        <Timeline
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onDayClick={handleDayClick}
          onYearChange={handleYearChange}
          events={events}
          hasEvents={hasEvents}
        />
        {/* Event section */}
        <div id="event-section" className="event-section">
          {selectedDay ? (
            <div className="event-container">
              <div className="event-header">
                <h2>
                  {months[selectedMonth]} {selectedDay}, {selectedYear} - {getPresidencyTerm()}
                  {dayInfo.dayNumber && ` - Day ${dayInfo.dayNumber} of Presidency`}
                </h2>
                <div className="flag-icon"></div>
              </div>
              
              {loading ? (
                <div className="loading-day-data">Loading day information...</div>
              ) : error ? (
                <div className="error-message">{error}</div>
              ) : selectedDayData && selectedDayData.active ? (
                <div className="day-data">
                  {/* Summary Section */}
                  {selectedDayData.summary && (
                    <div className="event-summary">
                      <h3>Summary</h3>
                      <p>{selectedDayData.summary}</p>
                    </div>
                  )}
                  
                  {/* Key Events Section */}
                  {selectedDayData.keyEvents && selectedDayData.keyEvents.length > 0 && (
                    <div className="key-events">
                      <h3>Key Events</h3>
                      <ul>
                        {selectedDayData.keyEvents.map((event, index) => (
                          <li key={index}>{event}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Tweets Section */}
                  {selectedDayData.tweets && selectedDayData.tweets.length > 0 && (
                    <div className="tweets-section">
                      <h3>Tweets</h3>
                      <div className="tweets-container">
                        {selectedDayData.tweets.map((tweet, index) => (
                          <div key={index} className="tweet">
                            <p>{tweet.content}</p>
                            <div className="tweet-date">{tweet.timestamp}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="no-events">No recorded activities for this day.</p>
              )}
            </div>
          ) : (
            <div className="select-day-prompt">
              <p>Select a day to view presidential activities</p>
              <div className="flag-icon-small"></div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default App;